/**
 * useGenerationDrafts — live subscription to draft items inside a single
 * generation run's subcollection. Used by GenerationReviewDialog to render
 * the accept/reject checklist.
 *
 * Sprint 15 — review-and-apply pattern.
 *
 * Firestore paths:
 *   entity='theme' → projects/{pid}/theme_generations/{gen}/generatedThemes
 *   entity='topic' → projects/{pid}/topic_generations/{gen}/generatedTopics
 *   entity='post'  → projects/{pid}/post_generations/{gen}/generatedPosts
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { GenerationEntity } from './useGenerationRuns';

const RUN_COLLECTIONS: Record<GenerationEntity, string> = {
  theme: 'theme_generations',
  topic: 'topic_generations',
  post:  'post_generations',
  adBriefTopic: 'ad_creative_brief_topic_generations',
};

const DRAFT_COLLECTIONS: Record<GenerationEntity, string> = {
  theme: 'generatedThemes',
  topic: 'generatedTopics',
  post:  'generatedPosts',
  adBriefTopic: 'generatedBriefs',
};

/**
 * Per-draft shape — `data` is entity-specific (ContentTheme | ContentTopic | ContentPost).
 * Callers cast `data` based on `entity` context.
 */
export interface GenerationDraft<TData = Record<string, unknown>> {
  id: string;
  entityType: 'theme' | 'topic' | 'post' | 'adBriefTopic';
  data: TData;
  accepted: boolean | null;
  /** Post drafts denormalise these for faster review rendering */
  platform?: string;
  topicId?: string;
  /** Theme drafts carry these */
  anchorAudienceIds?: string[];
  anchorInsightIds?: string[];
  whyForProject?: string;
  createdAt?: Date;
  appliedAt?: Date;
  [extra: string]: unknown;
}

export interface UseGenerationDraftsReturn<T> {
  drafts: GenerationDraft<T>[];
  loading: boolean;
  error: Error | null;
}

function tsToDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return undefined;
}

export function useGenerationDrafts<T = Record<string, unknown>>(
  projectId: string | null,
  entity: GenerationEntity,
  generationId: string | null,
): UseGenerationDraftsReturn<T> {
  const [drafts, setDrafts] = useState<GenerationDraft<T>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !generationId) {
      setDrafts([]); setLoading(false); return;
    }
    setLoading(true);
    const db = CoreFirebaseService.getInstance().firestore;
    const coll = collection(
      db,
      'projects', projectId,
      RUN_COLLECTIONS[entity], generationId,
      DRAFT_COLLECTIONS[entity],
    );
    // Posts subcollection has denormalised `topicId`/`platform` that make ordering
    // by createdAt a bit noisy — but for review UX order of arrival is fine.
    const q = query(coll, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: GenerationDraft<T>[] = snap.docs.map((d) => {
          const raw = d.data() as Record<string, unknown>;
          return {
            ...raw,
            id: d.id,
            entityType: (raw.entityType as GenerationDraft['entityType']) ?? entity,
            data: (raw.data as T) ?? ({} as T),
            accepted: raw.accepted === undefined ? null : (raw.accepted as boolean | null),
            createdAt: tsToDate(raw.createdAt),
            appliedAt: tsToDate(raw.appliedAt),
          } as GenerationDraft<T>;
        });
        setDrafts(list);
        setLoading(false);
      },
      (err) => { setError(err as Error); setLoading(false); },
    );
    return () => unsub();
  }, [projectId, entity, generationId]);

  return { drafts, loading, error };
}
